import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/utils'

/**
 * PageHeader : primitive produit semantique.
 *
 * Utilisation :
 *   <PageHeader title="Clients">
 *     <Button>Nouveau client</Button>
 *   </PageHeader>
 *
 * Slots :
 *   title      : string ou ReactNode, titre H1 de page
 *   breadcrumb : ReactNode optionnel, trail au-dessus du titre
 *   children   : ReactNode, actions a droite (boutons, selects, etc.)
 *
 * REGLE DS (interdit, cf docs/design-system/patterns.md) : PAS de sous-titre de
 * page. Le prop subtitle a ete retire volontairement. Le titre se suffit.
 *
 * Variantes :
 *   density   : 'comfortable' (defaut) ou 'compact'
 *   separator : true (defaut) ou false, divider bas qui separe du contenu
 */
const pageHeaderVariants = cva(
  'flex flex-col gap-3',
  {
    variants: {
      density: {
        comfortable: 'pb-6',
        compact:     'pb-4',
      },
      separator: {
        true:  'border-b border-ink-ghost/30 mb-6',
        false: 'mb-4',
      },
    },
    defaultVariants: {
      density: 'comfortable',
      separator: true,
    },
  }
)

export interface PageHeaderProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'title'>,
    VariantProps<typeof pageHeaderVariants> {
  title: React.ReactNode
  breadcrumb?: React.ReactNode
}

const PageHeader = React.forwardRef<HTMLElement, PageHeaderProps>(
  ({ className, density, separator, title, breadcrumb, children, ...props }, ref) => {
    return (
      <header
        ref={ref}
        className={cn(pageHeaderVariants({ density, separator }), className)}
        {...props}
      >
        {breadcrumb ? (
          <div className="text-caption text-ink-muted">{breadcrumb}</div>
        ) : null}

        <div className="flex items-start justify-between gap-6">
          <div className="min-w-0 flex-1 space-y-1">
            {typeof title === 'string' ? (
              <h1 className="text-h1 text-ink tracking-tight truncate">{title}</h1>
            ) : (
              title
            )}
          </div>

          {children ? (
            <div className="flex items-center gap-2 shrink-0">{children}</div>
          ) : null}
        </div>
      </header>
    )
  }
)
PageHeader.displayName = 'PageHeader'

export { PageHeader, pageHeaderVariants }
